/**
 * BibTeX Parser for Lilia Editor
 * Parses BibTeX files and entries into structured format
 */

export interface BibEntry {
  citeKey: string;
  entryType: string;
  fields: Record<string, string>;
  rawBibtex?: string;
}

export interface ParseResult {
  entries: BibEntry[];
  errors: string[];
}

/**
 * Parse a BibTeX string into structured entries
 */
export function parseBibTeX(bibtex: string): ParseResult {
  const entries: BibEntry[] = [];
  const errors: string[] = [];

  // Match BibTeX entries: @type{key, ... }
  const entryRegex = /@(\w+)\s*\{\s*([^,\s]+)\s*,([^@]*?)(?=\n\s*@|\s*$)/gs;

  let match;
  while ((match = entryRegex.exec(bibtex)) !== null) {
    try {
      const [fullMatch, entryType, citeKey, fieldsStr] = match;
      const fields = parseFields(fieldsStr);

      entries.push({
        citeKey: citeKey.trim(),
        entryType: entryType.toLowerCase(),
        fields,
        rawBibtex: fullMatch.trim(),
      });
    } catch (e) {
      errors.push(`Error parsing entry: ${(e as Error).message}`);
    }
  }

  return { entries, errors };
}

/**
 * Parse field string into key-value pairs
 */
function parseFields(fieldsStr: string): Record<string, string> {
  const fields: Record<string, string> = {};

  // Match field = value patterns
  // Handles: field = {value}, field = "value", field = number
  const fieldRegex = /(\w+)\s*=\s*(?:\{([^{}]*(?:\{[^{}]*\}[^{}]*)*)\}|"([^"]*)"|(\d+))/g;

  let match;
  while ((match = fieldRegex.exec(fieldsStr)) !== null) {
    const [, fieldName, bracedValue, quotedValue, numValue] = match;
    const value = bracedValue ?? quotedValue ?? numValue ?? "";
    fields[fieldName.toLowerCase()] = cleanFieldValue(value);
  }

  return fields;
}

/**
 * Clean field value by removing extra braces and normalizing whitespace
 */
function cleanFieldValue(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .replace(/^\{|\}$/g, "")
    .trim();
}

/**
 * Convert BibEntry to BibTeX string
 */
export function entryToBibTeX(entry: BibEntry): string {
  const lines = [`@${entry.entryType}{${entry.citeKey},`];

  for (const [key, value] of Object.entries(entry.fields)) {
    lines.push(`  ${key} = {${value}},`);
  }

  lines.push("}");
  return lines.join("\n");
}

/**
 * Format a citation for display (e.g., "Einstein, 1905")
 */
export function formatCitation(entry: BibEntry, style: "author-year" | "numeric" = "author-year"): string {
  const { fields } = entry;

  if (style === "numeric") {
    return entry.citeKey;
  }

  // Author-year style
  const author = fields.author || "Unknown";
  const year = fields.year || "n.d.";

  // Extract first author's last name
  const firstAuthor = author.split(" and ")[0];
  const lastName = firstAuthor.includes(",")
    ? firstAuthor.split(",")[0].trim()
    : firstAuthor.split(" ").pop()?.trim() || firstAuthor;

  return `${lastName}, ${year}`;
}

/**
 * Format a full bibliography entry for display
 */
export type CitationStyleId = "apa" | "mla" | "chicago" | "ieee" | "harvard" | "vancouver";

export const CITATION_STYLES: { id: CitationStyleId; name: string; description: string }[] = [
  { id: "apa", name: "APA 7th Edition", description: "Psychology, education, social sciences" },
  { id: "mla", name: "MLA 9th Edition", description: "Humanities" },
  { id: "chicago", name: "Chicago 17th Edition", description: "History, some sciences" },
  { id: "ieee", name: "IEEE", description: "Engineering, computer science" },
  { id: "harvard", name: "Harvard", description: "UK, Australia" },
  { id: "vancouver", name: "Vancouver", description: "Medicine, health sciences" },
];

export function formatBibliographyEntry(entry: BibEntry, style: CitationStyleId = "apa"): string {
  const { fields, entryType } = entry;

  switch (style) {
    case "apa":
      return formatAPA(fields, entryType);
    case "mla":
      return formatMLA(fields, entryType);
    case "chicago":
      return formatChicago(fields, entryType);
    case "ieee":
      return formatIEEE(fields, entryType);
    case "harvard":
      return formatHarvard(fields, entryType);
    case "vancouver":
      return formatVancouver(fields, entryType);
    default:
      return formatAPA(fields, entryType);
  }
}

function formatAPA(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const year = fields.year || "n.d.";
  const title = fields.title || "Untitled";

  const parts = [`${formatAuthorAPA(author)} (${year}).`];

  if (entryType === "article") {
    parts.push(`${title}.`);
    if (fields.journal) {
      parts.push(`<em>${fields.journal}</em>`);
      if (fields.volume) parts.push(`, ${fields.volume}`);
      if (fields.number) parts.push(`(${fields.number})`);
      if (fields.pages) parts.push(`, ${fields.pages}`);
      parts.push(".");
    }
  } else if (entryType === "book") {
    parts.push(`<em>${title}</em>.`);
    if (fields.publisher) {
      if (fields.address) parts.push(`${fields.address}:`);
      parts.push(`${fields.publisher}.`);
    }
  } else {
    parts.push(`${title}.`);
  }

  if (fields.doi) {
    parts.push(`https://doi.org/${fields.doi}`);
  }

  return parts.join(" ");
}

function formatMLA(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const title = fields.title || "Untitled";
  const year = fields.year || "n.d.";

  const parts = [`${formatAuthorMLA(author)}.`];
  parts.push(`"${title}."`);

  if (entryType === "article" && fields.journal) {
    parts.push(`<em>${fields.journal}</em>,`);
    if (fields.volume) parts.push(`vol. ${fields.volume},`);
    if (fields.number) parts.push(`no. ${fields.number},`);
    parts.push(`${year},`);
    if (fields.pages) parts.push(`pp. ${fields.pages}.`);
  } else if (entryType === "book") {
    if (fields.publisher) parts.push(`${fields.publisher},`);
    parts.push(`${year}.`);
  }

  return parts.join(" ");
}

function formatChicago(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const title = fields.title || "Untitled";
  const year = fields.year || "n.d.";

  const parts = [`${formatAuthorChicago(author)}.`];
  parts.push(`"${title}."`);

  if (entryType === "article" && fields.journal) {
    parts.push(`<em>${fields.journal}</em>`);
    if (fields.volume) parts.push(`${fields.volume}`);
    if (fields.number) parts.push(`, no. ${fields.number}`);
    parts.push(`(${year})`);
    if (fields.pages) parts.push(`: ${fields.pages}`);
    parts.push(".");
  } else if (entryType === "book") {
    if (fields.address) parts.push(`${fields.address}:`);
    if (fields.publisher) parts.push(`${fields.publisher},`);
    parts.push(`${year}.`);
  }

  return parts.join(" ");
}

/**
 * Split one BibTeX author into a surname and its given names.
 *
 * Every style formatter used to do this inline, and every one of them assumed
 * a given name existed. For a mononym -- "Euclid", "Aristotle", an
 * organisation -- `parts.pop()` empties the array, the initials come out
 * empty, and the separator is emitted anyway:
 *
 *     APA        "Euclid, "   -> "Euclid,  (c. 300 BCE)."   <- the stray comma
 *     Harvard    "Euclid, "
 *     IEEE       " Euclid"
 *     Vancouver  "Euclid "
 *
 * A trailing "Euclid," with nothing after it hit the same wall from the other
 * direction, producing "Euclid, .". One helper now answers the question once,
 * and each formatter decides what to do when there are no given names.
 */
function splitName(author: string): { last: string; givens: string[] } {
  const a = author.trim();
  if (a.includes(",")) {
    const [last = "", first = ""] = a.split(",").map((x) => x.trim());
    return { last, givens: first ? first.split(/\s+/).filter(Boolean) : [] };
  }
  const parts = a.split(/\s+/).filter(Boolean);
  const last = parts.pop() ?? "";
  return { last, givens: parts };
}

function formatAuthorAPA(author: string): string {
  const authors = author.split(" and ");
  return authors
    .map((a) => {
      const { last, givens } = splitName(a);
      if (givens.length === 0) return last;
      return `${last}, ${givens.map((g) => `${g.charAt(0)}.`).join(" ")}`;
    })
    .join(", & ");
}

function formatAuthorMLA(author: string): string {
  const authors = author.split(" and ");
  if (authors.length === 1) {
    return authors[0].trim();
  }
  return `${authors[0].trim()}, et al`;
}

function formatAuthorChicago(author: string): string {
  return formatAuthorMLA(author);
}

function formatIEEE(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const title = fields.title || "Untitled";
  const year = fields.year || "n.d.";

  const parts = [`${formatAuthorIEEE(author)},`];
  parts.push(`"${title},"`);

  if (entryType === "article" && fields.journal) {
    parts.push(`<em>${fields.journal}</em>`);
    if (fields.volume) parts.push(`, vol. ${fields.volume}`);
    if (fields.number) parts.push(`, no. ${fields.number}`);
    if (fields.pages) parts.push(`, pp. ${fields.pages}`);
    parts.push(`, ${year}.`);
  } else if (entryType === "book") {
    if (fields.address) parts.push(`${fields.address}:`);
    if (fields.publisher) parts.push(`${fields.publisher}`);
    parts.push(`, ${year}.`);
  } else if (entryType === "inproceedings" || entryType === "conference") {
    if (fields.booktitle) {
      parts.push(`in <em>${fields.booktitle}</em>`);
      parts.push(`, ${year}`);
      if (fields.pages) parts.push(`, pp. ${fields.pages}`);
      parts.push(".");
    }
  } else {
    parts.push(`${year}.`);
  }

  return parts.join(" ");
}

function formatAuthorIEEE(author: string): string {
  const authors = author.split(" and ");
  const formatted = authors.map((a) => {
    const { last, givens } = splitName(a);
    if (givens.length === 0) return last;
    return `${givens.map((g) => `${g.charAt(0)}.`).join(" ")} ${last}`;
  });

  if (formatted.length <= 3) {
    if (formatted.length === 1) return formatted[0];
    return formatted.slice(0, -1).join(", ") + " and " + formatted[formatted.length - 1];
  }
  return formatted.slice(0, 3).join(", ") + ", <em>et al.</em>";
}

function formatHarvard(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const year = fields.year || "n.d.";
  const title = fields.title || "Untitled";

  const parts = [`${formatAuthorHarvard(author)}`];
  parts.push(`(${year})`);

  if (entryType === "article") {
    parts.push(`'${title}',`);
    if (fields.journal) {
      parts.push(`<em>${fields.journal}</em>`);
      if (fields.volume) {
        parts.push(`, ${fields.volume}`);
        if (fields.number) parts.push(`(${fields.number})`);
      }
      if (fields.pages) parts.push(`, pp. ${fields.pages}`);
      parts.push(".");
    }
  } else if (entryType === "book") {
    parts.push(`<em>${title}</em>,`);
    if (fields.address) parts.push(`${fields.address}:`);
    if (fields.publisher) parts.push(`${fields.publisher}.`);
  } else {
    parts.push(`${title}.`);
  }

  return parts.join(" ");
}

function formatAuthorHarvard(author: string): string {
  const authors = author.split(" and ");
  const formatted = authors.map((a) => {
    const { last, givens } = splitName(a);
    if (givens.length === 0) return last;
    return `${last}, ${givens.map((g) => `${g.charAt(0)}.`).join("")}`;
  });

  if (formatted.length === 1) return formatted[0];
  if (formatted.length === 2) return `${formatted[0]} and ${formatted[1]}`;
  if (formatted.length > 3) return `${formatted[0]} et al.`;
  return formatted.slice(0, -1).join(", ") + " and " + formatted[formatted.length - 1];
}

function formatVancouver(fields: Record<string, string>, entryType: string): string {
  const author = fields.author || "Unknown";
  const title = fields.title || "Untitled";
  const year = fields.year || "n.d.";

  const parts = [`${formatAuthorVancouver(author)}`];
  parts.push(`${title}.`);

  if (entryType === "article" && fields.journal) {
    parts.push(`${fields.journal}.`);
    parts.push(`${year}`);
    if (fields.volume) {
      parts.push(`;${fields.volume}`);
      if (fields.number) parts.push(`(${fields.number})`);
    }
    if (fields.pages) parts.push(`:${fields.pages}`);
    parts.push(".");
  } else if (entryType === "book") {
    if (fields.address) parts.push(`${fields.address}:`);
    if (fields.publisher) parts.push(`${fields.publisher};`);
    parts.push(`${year}.`);
  } else {
    parts.push(`${year}.`);
  }

  return parts.join(" ");
}

function formatAuthorVancouver(author: string): string {
  const authors = author.split(" and ");
  const formatted = authors.slice(0, 6).map((a) => {
    const { last, givens } = splitName(a);
    if (givens.length === 0) return last;
    return `${last} ${givens.map((g) => g.charAt(0).toUpperCase()).join("")}`;
  });

  if (authors.length > 6) {
    formatted.push("et al");
  }

  return formatted.join(", ") + ".";
}

/**
 * Common entry types
 */
export const ENTRY_TYPES = [
  "article",
  "book",
  "booklet",
  "conference",
  "inbook",
  "incollection",
  "inproceedings",
  "manual",
  "mastersthesis",
  "misc",
  "phdthesis",
  "proceedings",
  "techreport",
  "unpublished",
] as const;

/**
 * Common fields for each entry type
 */
export const REQUIRED_FIELDS: Record<string, string[]> = {
  article: ["author", "title", "journal", "year"],
  book: ["author", "title", "publisher", "year"],
  inproceedings: ["author", "title", "booktitle", "year"],
  phdthesis: ["author", "title", "school", "year"],
  misc: ["title"],
};

export const OPTIONAL_FIELDS: Record<string, string[]> = {
  article: ["volume", "number", "pages", "month", "doi", "url"],
  book: ["volume", "series", "address", "edition", "month", "isbn"],
  inproceedings: ["editor", "volume", "series", "pages", "address", "organization", "publisher"],
  phdthesis: ["address", "month"],
  misc: ["author", "howpublished", "month", "year", "note"],
};
